import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/apiClient';
import {
  RefreshCw, TrendingUp, TrendingDown, CreditCard,
  ShoppingCart, Users, BarChart3, AlertTriangle, ChevronLeft
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { cur } from '@/lib/currency';
const PERIODS = [
  { key: 'today', label: 'اليوم' },
  { key: 'week', label: 'أسبوع' },
  { key: 'month', label: 'شهر' },
  { key: 'quarter', label: 'ربع سنة' },
  { key: 'ytd', label: 'هذا العام' },
];

const fmt = (v) => (v || 0).toLocaleString('ar-EG', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtMoney = (v) => `${fmt(v)} ${cur()}`;

function ExecutiveKpi({ title, value, subtitle, icon: Icon, color = 'text-primary', bg = 'bg-primary/10', onClick }) {
  return (
    <div
      onClick={onClick}
      className={`bg-card border border-border rounded-2xl p-4 shadow-sm relative transition-all duration-200
        ${onClick ? 'cursor-pointer hover:border-primary hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]' : ''}`}
    >
      {onClick && <ChevronLeft className="absolute top-3 left-3 w-3.5 h-3.5 text-primary opacity-50" />}
      <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center mb-3`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="text-xs text-muted-foreground font-medium mb-1">{title}</p>
      <p className="text-2xl font-black text-foreground leading-tight">{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/sales/dashboard/manager', { period: p });
      if (res?.success) setData(res.data);
      else setError(res?.error || 'فشل التحميل');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(period); }, [period]);

  const agingData = data ? [
    { label: 'جاري', value: data.ar_aging?.current || 0 },
    { label: '0-30 يوم', value: data.ar_aging?.d30 || 0 },
    { label: '31-60 يوم', value: data.ar_aging?.d60 || 0 },
    { label: '61-90 يوم', value: data.ar_aging?.d90 || 0 },
    { label: '+90 يوم', value: data.ar_aging?.d90plus || 0 },
  ] : [];

  const AGING_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#f97316', '#ef4444'];

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-black text-foreground">لوحة تحكم المدير</h1>
          <p className="text-muted-foreground text-xs mt-0.5">نظرة شاملة على الأداء</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-xl p-1 gap-1">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  period === p.key ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={() => loadData(period)} disabled={loading} className="p-2 rounded-xl border border-border bg-card">
            <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">⚠️ {error}</div>
      )}

      {/* Skeleton */}
      {loading && !data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-28 bg-muted rounded-2xl animate-pulse" />)}
        </div>
      )}

      {data && (
        <>
          {/* Executive KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <ExecutiveKpi title="إجمالي المبيعات" value={fmtMoney(data.total_revenue)}
              subtitle={`${fmt(data.orders_count)} طلب`} icon={TrendingUp} color="text-primary" bg="bg-primary/10" onClick={() => navigate('/orders')} />
            <ExecutiveKpi title="إجمالي التحصيلات" value={fmtMoney(data.total_collections)}
              subtitle={`كفاءة ${data.collection_efficiency?.toFixed(1)}%`} icon={CreditCard} color="text-secondary" bg="bg-secondary/10" onClick={() => navigate('/collections')} />
            <ExecutiveKpi title="الذمم المدينة" value={fmtMoney(data.total_ar)}
              subtitle="إجمالي غير مسدد" icon={AlertTriangle} color="text-red-500" bg="bg-red-50" onClick={() => navigate('/collections')} />
            <ExecutiveKpi title="متوسط قيمة الطلب" value={fmtMoney(data.avg_order_value)}
              subtitle="AOV" icon={BarChart3} color="text-blue-600" bg="bg-blue-50" onClick={() => navigate('/orders')} />
            <ExecutiveKpi title="طلبات معلقة" value={fmt(data.pending_orders)}
              subtitle="بانتظار التأكيد" icon={ShoppingCart} color="text-amber-600" bg="bg-amber-50" onClick={() => navigate('/orders')} />
            <ExecutiveKpi title="طلبات ملغية" value={fmt(data.cancelled_orders)}
              subtitle="خلال الفترة" icon={ShoppingCart} color="text-red-500" bg="bg-red-50" onClick={() => navigate('/orders')} />
          </div>

          {/* Revenue Trend */}
          {data.monthly_trend?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-foreground mb-4 text-sm">تطور الإيرادات</h3>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.monthly_trend} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="mgrGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false}
                      tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                    <Tooltip formatter={(v) => [`${v.toLocaleString('ar-EG', { maximumFractionDigits: 0 })} ${cur()}`, 'المبيعات']} />
                    <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#mgrGrad)" dot={{ r: 3 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* AR Aging */}
          <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-foreground mb-4 text-sm">تقادم الذمم المدينة</h3>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false}
                    tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v) => [`${v.toLocaleString('ar-EG', { maximumFractionDigits: 0 })} ${cur()}`]} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {agingData.map((_, i) => <Cell key={i} fill={AGING_COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Customers */}
          {data.top_customers?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-foreground mb-3 text-sm">أفضل العملاء</h3>
              <div className="space-y-2">
                {data.top_customers.map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs font-black text-muted-foreground w-5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                      <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                        <div className="bg-primary h-1.5 rounded-full"
                          style={{ width: `${Math.min(100, (c.total / (data.top_customers[0]?.total || 1)) * 100)}%` }} />
                      </div>
                    </div>
                    <span className="text-sm font-bold text-foreground shrink-0">{fmtMoney(c.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rep Performance */}
          {data.rep_performance?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-foreground mb-3 text-sm">أداء المناديب</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground text-xs border-b border-border">
                      <th className="text-right pb-2 font-medium">#</th>
                      <th className="text-right pb-2 font-medium">الاسم</th>
                      <th className="text-left pb-2 font-medium">المبيعات</th>
                      <th className="text-left pb-2 font-medium">الطلبات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.rep_performance.map((r) => (
                      <tr key={r.rank} className={`${r.rank <= 3 ? 'text-foreground' : 'text-muted-foreground'}`}>
                        <td className="py-2 pr-1">
                          <span className={`text-xs font-black ${r.rank === 1 ? 'text-amber-500' : r.rank === 2 ? 'text-slate-400' : r.rank === 3 ? 'text-amber-700' : ''}`}>
                            {r.rank}
                          </span>
                        </td>
                        <td className="py-2 font-medium text-sm">{r.name}</td>
                        <td className="py-2 text-left font-bold text-primary text-xs">{fmtMoney(r.sales)}</td>
                        <td className="py-2 text-left text-xs">{r.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}